import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mail, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export const EmailVerificationPending = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleResendEmail = async () => {
    setLoading(true);
    setSuccess(false);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/auth/resend-verification`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(true);
      toast.success('Bestätigungs-E-Mail wurde erneut gesendet!');
    } catch (error) {
      console.error('Error resending verification email:', error);
      
      if (error.response?.data?.detail === 'Email bereits bestätigt') {
        toast.info('Ihre E-Mail ist bereits bestätigt!');
        // Refresh page to update status
        window.location.reload();
      } else {
        toast.error('Fehler beim Versenden der E-Mail. Bitte versuchen Sie es später erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <Mail className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <CardTitle className="text-2xl font-bold">
            E-Mail-Bestätigung erforderlich
          </CardTitle>
          <CardDescription className="text-base">
            Bitte bestätigen Sie Ihre E-Mail-Adresse, um AllFlowX nutzen zu können.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Info Alert */}
          <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
            <Mail className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-sm text-blue-900 dark:text-blue-100">
              Wir haben Ihnen eine E-Mail an <strong>{user?.email}</strong> gesendet.
              Bitte klicken Sie auf den Bestätigungslink in der E-Mail.
            </AlertDescription>
          </Alert>

          {/* Success Message */}
          {success && (
            <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-sm text-green-900 dark:text-green-100">
                E-Mail wurde erfolgreich erneut versendet! Bitte überprüfen Sie Ihren Posteingang.
              </AlertDescription>
            </Alert>
          )}

          {/* Resend Button */}
          <div className="space-y-3">
            <Button
              onClick={handleResendEmail}
              disabled={loading}
              className="w-full"
              size="lg"
            >
              {loading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  E-Mail erneut senden
                </>
              )}
            </Button>

            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full"
              size="lg"
            >
              Abmelden
            </Button>
          </div>

          {/* Help Text */}
          <div className="text-center space-y-2 pt-4 border-t">
            <p className="text-sm text-muted-foreground">
              <strong>E-Mail nicht erhalten?</strong>
            </p>
            <ul className="text-xs text-muted-foreground space-y-1 text-left list-disc list-inside">
              <li>Überprüfen Sie Ihren Spam-Ordner</li>
              <li>Stellen Sie sicher, dass die E-Mail-Adresse korrekt ist</li>
              <li>Warten Sie einige Minuten und versuchen Sie es erneut</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailVerificationPending;
